import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import {of, tap} from 'rxjs';
import { switchMap, map, catchError, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import {AppState} from "../../../shared/state/app.state";
import {ReportSettingTemplateService} from "../../data-access/report-setting-template.service";
import {
  addReportSettingTemplate,
  addReportSettingTemplateFailure,
  addReportSettingTemplateSuccess,
  loadReportSettingTemplates,
  loadReportSettingTemplatesFailure,
  loadReportSettingTemplatesSuccess,
  modifyReportSettingTemplate,
  modifyReportSettingTemplateFailure,
  modifyReportSettingTemplateSuccess,
  removeReportSettingTemplate,
  removeReportSettingTemplateFailure,
  removeReportSettingTemplateSuccess,
} from "./report-template-setting.action";
import {selectReports} from "../report/report.selectors";
import {ToastMessageOptions, MessageService} from "primeng/api";
import {GetMessage} from "../../../global-config/utils/toast-messages.utils";


@Injectable()
export class ReportSettingTemplateEffects {

  loadReportSettingTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadReportSettingTemplates),
      switchMap(() =>
        this.reportSettingTemplateService.get()
          .pipe(
            map((templates) => loadReportSettingTemplatesSuccess({reportSettingTemplates: templates})),
            catchError((error) => of(loadReportSettingTemplatesFailure({ error: GetMessage('LoadFailure', 'Report Setting Template') })))
          )
      )
    )
  );

  addReportSettingTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addReportSettingTemplate),
      withLatestFrom(this.store.select(selectReports)),
      switchMap(([action, cadModels]) =>
        this.reportSettingTemplateService.create(action.reportSettingTemplate)
          .pipe(
            map((addedReportSettingTemplate) => addReportSettingTemplateSuccess({reportSettingTemplate: addedReportSettingTemplate, successMessage: GetMessage('AddSuccess', 'Report Setting Template')})),
            catchError((error) => of(addReportSettingTemplateFailure({ error:GetMessage('AddFailure', 'Report Setting Template') })))
          )
      )
    )
  );

  modifyReportSettingTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(modifyReportSettingTemplate),
      withLatestFrom(this.store.select(selectReports)),
      switchMap(([action, cadModels]) =>
        this.reportSettingTemplateService.modify(action.reportSettingTemplate)
          .pipe(
            map((modifiedReportSettingTemplate) => modifyReportSettingTemplateSuccess({reportSettingTemplate: modifiedReportSettingTemplate, successMessage: GetMessage('ModifySuccess', 'Report Setting Template')})),
            catchError((error) => of(modifyReportSettingTemplateFailure({ error: GetMessage('ModifyFailure', 'Report Setting Template') })))
          )
      )
    )
  );

  removeReportSettingTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeReportSettingTemplate),
      switchMap((action) =>
        this.reportSettingTemplateService.remove(action.reportSettingTemplateId)
          .pipe(
            map((reportSettingTemplateId) => removeReportSettingTemplateSuccess({reportSettingTemplateId: reportSettingTemplateId, successMessage: GetMessage('RemoveSuccess', 'Report Setting Template')})),
            catchError((error) => of(removeReportSettingTemplateFailure({ error: GetMessage('RemoveFailure', 'Report Setting Template') })))
          )
      )
    )
  );

  toastSuccessMessages$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          removeReportSettingTemplateSuccess,
          modifyReportSettingTemplateSuccess,
          addReportSettingTemplateSuccess
        ),
        tap(({ successMessage }) => {
            return this.messageService.add({
              severity: 'success',
              summary: successMessage,
            } as ToastMessageOptions)
          }
        )
      ),
    { dispatch: false }
  );

  toastErrorMessages$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          loadReportSettingTemplatesFailure,
          removeReportSettingTemplateFailure,
          modifyReportSettingTemplateFailure,
          loadReportSettingTemplatesFailure
        ),
        tap(({ error }) => {
            return this.messageService.add({
              severity: 'error',
              summary: error,
            } as ToastMessageOptions)
          }
        )
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private reportSettingTemplateService: ReportSettingTemplateService,
    private messageService: MessageService
  ) {}
}
